<template>
  <div class="d-flex flex-column">
    <div class="d-flex flex-column">
      <detail-label
        label="Current Duration:"
        :value="
          durationLabel(
            appointment.duration_minutes ||
              appointment.estimated_duration_minutes,
            appointment.service_category
          )
        "
      ></detail-label>
      <detail-label
        v-if="inProgress"
        label="Estimated Remaining:"
        :value="remainingDurationLabel(remainingMinutes)"
        :class="{ 'text-danger': remainingMinutes < 0 }"
      ></detail-label>
    </div>
    <div class="d-flex mt-2 flex-col md:flex-row gap-3">
      <div>
        <div class="d-flex align-items-center">
          <b>{{ fieldLabel }}</b>
          <div v-if="hasChanges" class="link ml-2" @click="clearChanges">
            Clear Changes
          </div>
        </div>
        <div class="d-flex align-items-center">
          <div class="d-flex align-items-center mr-2">
            <Select
              v-model="hours"
              placeholder="0"
              class="duration-select"
              :options="hourOptions"
              full-width
            ></Select>
            <div class="ml-1">Hours</div>
          </div>
          <div class="d-flex align-items-center">
            <Select
              v-model="minutes"
              placeholder="00"
              class="duration-select"
              :options="minuteOptions"
              full-width
            ></Select>
            <div class="ml-1">Minutes</div>
          </div>
        </div>
      </div>
      <div class="durations">
        <div v-if="!inProgress">
          <i class="fas fa-clock mr-1"></i>
          Duration: {{ duration }}
        </div>
        <div>
          <i class="fas fa-clock mr-1"></i>
          Start Time at {{ formatTime(appointmentStart) }}
        </div>
        <div>
          <i class="fas fa-clock mr-1"></i>
          Estimated End Time at {{ endTime }}
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import moment from "moment-timezone";

import DetailLabel from "@/components/DetailLabel.vue";
import CONSTANTS from "@/Constants.js";
const {
  DURATION_HOUR_OPTIONS,
  DURATION_MINUTE_OPTIONS,
  APPOINTMENT_EDIT_MODE,
  EDIT_MODE_IN_PROGRESS
} = CONSTANTS;

export default {
  components: {
    DetailLabel
  },
  props: {
    appointment: {
      type: Object,
      default: null
    },
    selectedServiceCategory: {
      type: Object,
      default: null
    },
    startTime: {
      type: String,
      default: ""
    },
    modelValue: {
      // duration_minutes
      type: Number,
      default: 0
    }
  },
  emits: ["update:modelValue", "clear"],
  data() {
    return {
      hourOptions: DURATION_HOUR_OPTIONS,
      minuteOptions: DURATION_MINUTE_OPTIONS,
      hours: null,
      minutes: null
    };
  },
  computed: {
    formMode() {
      return APPOINTMENT_EDIT_MODE[this.appointment.status];
    },
    inProgress() {
      return this.formMode === EDIT_MODE_IN_PROGRESS;
    },
    valueLabel() {
      return this.isInProgress ? "Remaining Duration:" : "Current Duration:";
    },
    duration() {
      return this.durationLabel(this.modelValue, this.selectedServiceCategory);
    },
    currentDuration() {
      return (
        this.appointment.duration_minutes ||
        this.appointment.estimated_duration_minutes
      );
    },
    endTime() {
      if (this.hasChanges || this.selectedServiceCategory) {
        const startTime = moment(this.appointmentStart);
        if (
          startTime.isAfter(moment()) ||
          this.appointment.status === "pending"
        ) {
          // If appointment hasn't started, add duration to start time
          return this.formatTime(
            moment(startTime).add(this.modelValue, "minutes")
          );
        }
        // if appointment has already started, add remaining_duration to now
        return this.formatTime(moment().add(this.modelValue, "minutes"));
      }
      // If there have been no manual changes, display original estimated end time
      return this.formatTime(
        moment(this.appointmentStart).add(this.currentDuration, "minutes")
      );
    },
    appointmentStart() {
      const { dispatch_time, min_arrival_time } = this.appointment;
      return this.startTime || dispatch_time || min_arrival_time;
    },
    hasChanges() {
      return this.hours || this.minutes;
    },
    fieldLabel() {
      return this.inProgress
        ? "Set Estimated Remaining To:"
        : "Change Duration To:";
    },
    remainingMinutes() {
      return this.calculateRemainingDuration(this.appointment);
    }
  },
  watch: {
    hours(newValue, oldValue) {
      if (newValue !== oldValue) {
        this.setDuration();
      }
    },
    minutes(newValue, oldValue) {
      if (newValue !== oldValue) {
        this.setDuration();
      }
    }
  },
  methods: {
    clearChanges() {
      this.hours = null;
      this.minutes = null;
    },
    setDuration() {
      let durationMinutes = this.hours * 60 + this.minutes;
      if (!durationMinutes) {
        durationMinutes = this.currentDuration;
        this.$emit("clear");
        return;
      }
      this.$emit("update:modelValue", durationMinutes);
    }
  }
};
</script>

<style lang="scss" scoped>
.duration-select {
  width: 58px;
}
.durations {
  border-left: 1px solid var(--gray-border-color);
  margin-left: 15px;
  padding-left: 15px;
}
</style>
